import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mail, Lock } from "lucide-react";

/* Optional: set a real construction photo here (e.g. "/brand/construction-bg.jpg").
   Leave it as "" to use the built-in 3D construction scene (no vehicles). */
const BG_IMAGE = "/brand/background.png";
/* 👉 Put sitera-logo.png in public/brand/ */
const LOGO = "/brand/sitera-logo1.png";

const FONT = "'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif";
const NAVY = "#0A1F4D";
const GOLD = "#C9A043";
const BORDER = "#DDE3EF";
const MUTED = "#667391";

const R = (n, f) => Array.from({ length: n }, (_, i) => f(i));
const C30 = 0.866;
const P = (pts) => pts.map((p) => p.join(",")).join(" ");
const GOLD_HI = "#E4BF6A";

/* ---------- 3D (isometric) scene ---------- */
/* One isometric tower. (px, py) = screen position of its nearest bottom corner. */
function Block({ px, py, w, d, h, fade = 1 }) {
  const lx = px - w * C30, ly = py - w / 2;               // left-bottom corner
  const rx = px + d * C30, ry = py - d / 2;               // right-bottom corner
  const bx = px + (d - w) * C30, by = py - (w + d) / 2;   // back-bottom corner
  const L = P([[lx, ly], [px, py], [px, py - h], [lx, ly - h]]);
  const Rt = P([[px, py], [rx, ry], [rx, ry - h], [px, py - h]]);
  const T = P([[px, py - h], [rx, ry - h], [bx, by - h], [lx, ly - h]]);
  return (
    <g opacity={fade}>
      <polygon points={L} fill="url(#sl-faceL)" />
      <polygon points={L} fill="url(#sl-winL)" />
      <polygon points={L} fill="url(#sl-litL)" />
      <polygon points={Rt} fill="url(#sl-faceR)" />
      <polygon points={Rt} fill="url(#sl-winR)" />
      <polygon points={Rt} fill="url(#sl-litR)" />
      <polygon points={T} fill="url(#sl-top)" />
      <path d={`M${lx} ${ly - h}L${px} ${py - h}L${rx} ${ry - h}M${px} ${py}V${py - h}`} stroke="rgba(228,191,106,.55)" strokeWidth="1.2" fill="none" />
    </g>
  );
}

/* Building under construction: concrete slabs + columns, lower floors clad in glass. */
function Frame({ px, py, w, d, floors, fh = 44, clad = 2 }) {
  const lx = px - w * C30, ly = py - w / 2;
  const rx = px + d * C30, ry = py - d / 2;
  const bx = px + (d - w) * C30, by = py - (w + d) / 2;
  const H = floors * fh;
  return (
    <g>
      <path d={`M${bx} ${by}V${by - H}`} stroke="#3B5FA5" strokeWidth="2" />
      <polygon points={P([[lx, ly], [px, py], [px, py - clad * fh], [lx, ly - clad * fh]])} fill="rgba(120,170,255,.28)" />
      <polygon points={P([[px, py], [rx, ry], [rx, ry - clad * fh], [px, py - clad * fh]])} fill="rgba(60,110,200,.30)" />
      {R(floors + 1, (k) => (
        <polygon key={k} points={P([[px, py - k * fh], [rx, ry - k * fh], [bx, by - k * fh], [lx, ly - k * fh]])}
          fill="rgba(70,120,210,.22)" stroke="rgba(228,191,106,.65)" strokeWidth="1.2" />
      ))}
      <path d={`M${lx} ${ly}V${ly - H}M${px} ${py}V${py - H}M${rx} ${ry}V${ry - H}`} stroke="#6C93DB" strokeWidth="2.2" />
    </g>
  );
}

/* Tower crane. (mx, my) = base on screen, h = mast height, dir = +1 jib to the left, -1 to the right. */
function Crane({ mx, my, h, dir = 1, jib = 240 }) {
  const top = my - h;
  const jx = mx - dir * jib * C30, jy = top + jib / 2;
  const cj = jib * 0.35;
  const cx = mx + dir * cj * C30, cy = top - cj / 2;
  const tx = mx + (jx - mx) * 0.72, ty = top + (jy - top) * 0.72 + 9;
  const zig = R(Math.floor(h / 34), (i) => `${i % 2 ? mx + 8 : mx - 8} ${my - i * 34}`).join("L");
  return (
    <g stroke={GOLD_HI} fill="none" strokeLinejoin="round">
      <path d={`M${mx - 8} ${my}V${top}M${mx + 8} ${my}V${top}`} strokeWidth="2.6" />
      <path d={`M${zig}`} strokeWidth="1.2" opacity=".8" />
      <path d={`M${cx} ${cy}L${mx} ${top}L${jx} ${jy}M${cx} ${cy + 9}L${mx} ${top + 9}L${jx} ${jy + 9}`} strokeWidth="2.2" />
      <path d={`M${mx} ${top - 56}L${jx} ${jy}M${mx} ${top - 56}L${cx} ${cy}M${mx} ${top - 56}V${top}`} strokeWidth="1.2" />
      <rect x={cx - 12} y={cy - 2} width="24" height="16" fill={GOLD_HI} stroke="none" />
      <rect x={mx - 12} y={top + 6} width="24" height="18" fill="#0A1F4D" strokeWidth="1.6" />
      <path d={`M${tx} ${ty}V${ty + 150}M${tx} ${ty + 150}L${tx - 26} ${ty + 166}M${tx} ${ty + 150}L${tx + 26} ${ty + 166}`} strokeWidth="1.2" />
      <rect x={tx - 34} y={ty + 166} width="68" height="8" fill={GOLD_HI} stroke="none" />
    </g>
  );
}

const FAR = [[70,640,60,60,170],[190,610,50,70,240],[300,650,70,50,150],[390,620,50,50,210],
             [1060,610,60,60,230],[1330,610,60,70,270],[1410,645,60,60,180]];
const MID = [[120,765,80,80,300],[262,742,70,90,390],[405,782,80,60,210],
             [1000,790,80,70,250],[1370,790,70,80,300]];

function Scene() {
  return (
    <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} viewBox="0 0 1440 900" preserveAspectRatio="xMidYMax slice" aria-hidden="true">
      <defs>
        <linearGradient id="sl-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#030816" /><stop offset=".6" stopColor="#0A1D47" /><stop offset="1" stopColor="#12306E" />
        </linearGradient>
        <radialGradient id="sl-glow" cx=".5" cy=".7" r=".55">
          <stop offset="0" stopColor="#C9A043" stopOpacity=".2" /><stop offset="1" stopColor="#C9A043" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="sl-vig" cx=".5" cy=".5" r=".55">
          <stop offset="0" stopColor="#0A1D47" stopOpacity=".45" /><stop offset="1" stopColor="#0A1D47" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="sl-gfade" x1="0" y1="0" x2="0" y2="1">
          <stop offset=".5" stopColor="#000" /><stop offset=".85" stopColor="#fff" />
        </linearGradient>
        <mask id="sl-gm"><rect width="1440" height="900" fill="url(#sl-gfade)" /></mask>
        <linearGradient id="sl-faceL" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#2F63BF" /><stop offset="1" stopColor="#12306E" /></linearGradient>
        <linearGradient id="sl-faceR" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#14306E" /><stop offset="1" stopColor="#050E2A" /></linearGradient>
        <linearGradient id="sl-top" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#5C8CDA" /><stop offset="1" stopColor="#2A56A8" /></linearGradient>
        {/* window grids, skewed to follow each face */}
        <pattern id="sl-winL" width="16" height="24" patternUnits="userSpaceOnUse" patternTransform="matrix(0.866 0.5 0 1 0 0)"><rect x="3" y="5" width="9" height="13" fill="rgba(170,205,255,.20)" /></pattern>
        <pattern id="sl-winR" width="16" height="24" patternUnits="userSpaceOnUse" patternTransform="matrix(0.866 -0.5 0 1 0 0)"><rect x="3" y="5" width="9" height="13" fill="rgba(140,180,240,.12)" /></pattern>
        <pattern id="sl-litL" width="80" height="96" patternUnits="userSpaceOnUse" patternTransform="matrix(0.866 0.5 0 1 0 0)">
          <rect x="19" y="5" width="9" height="13" fill="rgba(228,191,106,.6)" /><rect x="51" y="29" width="9" height="13" fill="rgba(228,191,106,.6)" /><rect x="3" y="77" width="9" height="13" fill="rgba(228,191,106,.6)" />
        </pattern>
        <pattern id="sl-litR" width="80" height="96" patternUnits="userSpaceOnUse" patternTransform="matrix(0.866 -0.5 0 1 0 0)">
          <rect x="35" y="53" width="9" height="13" fill="rgba(228,191,106,.5)" /><rect x="3" y="29" width="9" height="13" fill="rgba(228,191,106,.5)" />
        </pattern>
      </defs>

      <rect width="1440" height="900" fill="url(#sl-sky)" />
      <rect width="1440" height="900" fill="url(#sl-glow)" />

      {/* isometric ground grid */}
      <g mask="url(#sl-gm)" stroke="rgba(228,191,106,.17)" strokeWidth="1">
        {R(19, (i) => { const b = -340 + i * 70; return <line key={"a" + i} x1="0" y1={b} x2="1440" y2={b + 831} />; })}
        {R(19, (i) => { const b = 500 + i * 70; return <line key={"b" + i} x1="0" y1={b} x2="1440" y2={b - 831} />; })}
      </g>

      {/* distant towers (hazed) */}
      {FAR.map(([px, py, w, d, h]) => <Block key={"f" + px} px={px} py={py} w={w} d={d} h={h} fade={0.5} />)}
      {/* main towers */}
      {MID.map(([px, py, w, d, h]) => <Block key={"m" + px} px={px} py={py} w={w} d={d} h={h} />)}
      {/* building under construction + cranes */}
      <Frame px={1190} py={770} w={100} d={90} floors={8} />
      <Crane mx={1290} my={810} h={520} dir={1} jib={270} />
      <Crane mx={470} my={800} h={430} dir={1} jib={230} />

      <rect width="1440" height="900" fill="url(#sl-vig)" />
    </svg>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Enter your email and password to sign in."); return; }
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) { toast.error("Enter a valid email address."); return; }
    setBusy(true);
    try {
      /* 👉 Keep your EXISTING sign-in logic here (API call / auth context / redirect).
         Example: await login(email, password, remember); navigate("/dashboard"); */
    } catch (err) {
      toast.error("Sign-in failed. Check your email and password and try again.");
    } finally {
      setBusy(false);
    }
  };

  const input = {
    width: "100%", boxSizing: "border-box",
    background: "#F5F7FB", border: `1px solid ${BORDER}`,
    borderRadius: 9, height: 42, padding: "0 12px 0 38px", fontSize: 14, color: "#0B1630",
    outline: "none", fontFamily: FONT,
    transition: "border-color .2s, box-shadow .2s, background .2s",
  };
  const focus = (e) => { e.target.style.background = "#fff"; e.target.style.borderColor = NAVY; e.target.style.boxShadow = "0 0 0 3px rgba(201,160,67,0.35)"; };
  const blur = (e) => { e.target.style.background = "#F5F7FB"; e.target.style.borderColor = BORDER; e.target.style.boxShadow = "none"; };

  const label = { display: "block", fontSize: 12.5, fontWeight: 600, color: "#2B3652", marginBottom: 5 };
  const icon = { position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#8C97B0", pointerEvents: "none" };

  return (
    <div style={{
      position: "relative", overflow: "hidden",
      minHeight: "100dvh", width: "100%",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "16px", boxSizing: "border-box",
      fontFamily: FONT, background: "#040B1F",
    }}>
      <Scene />
      {BG_IMAGE && (
        <>
          <div style={{ position: "absolute", inset: 0, background: `url(${BG_IMAGE}) center/cover no-repeat` }} />
          <div style={{ position: "absolute", inset: 0, background: "rgba(4,11,31,0.55)" }} />
        </>
      )}

      <button
        type="button"
        onClick={() => navigate("/")}
        aria-label="Back to landing page"
        style={{
          position: "absolute", top: 18, left: 18, zIndex: 2,
          display: "inline-flex", alignItems: "center", gap: 8,
          background: "rgba(255,255,255,0.14)", color: "#fff",
          border: "1px solid rgba(255,255,255,0.35)", borderRadius: 10,
          padding: "8px 14px", fontSize: 13.5, fontWeight: 600, cursor: "pointer",
          backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
        }}
      >
        <ArrowLeft size={16} /> Back
      </button>

      <form
        onSubmit={onSubmit}
        noValidate
        style={{
          position: "relative", zIndex: 1,
          width: "100%", maxWidth: 380, maxHeight: "calc(100dvh - 32px)", overflowY: "auto",
          background: "rgba(255,255,255,0.98)",
          borderRadius: 16, padding: "30px 30px 22px",
          boxShadow: `0 40px 80px rgba(4,11,31,0.55), inset 0 2px 0 ${GOLD}`,
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 9, marginBottom: 16 }}>
          <img src={LOGO} alt="Sitera" style={{ height: 54, width: "auto", display: "block" }} />
          <div style={{ fontWeight: 800, fontSize: 22, letterSpacing: ".02em", color: NAVY, lineHeight: 1 }}>SITERA</div>
          <div style={{ fontSize: 12, color: MUTED, marginTop: -4 }}>Construction management platform</div>
        </div>

        <h1 style={{ margin: 0, fontSize: 19, fontWeight: 700, color: "#0B1630", textAlign: "center" }}>Welcome back</h1>
        <p style={{ margin: "4px 0 18px", fontSize: 13, color: MUTED, textAlign: "center" }}>
          Sign in to your project command center
        </p>

        <div style={{ marginBottom: 12 }}>
          <label htmlFor="email" style={label}>Work email</label>
          <div style={{ position: "relative" }}>
            <Mail size={16} style={icon} />
            <input
              id="email" type="email" value={email} placeholder="name@company.com"
              autoComplete="email" onChange={(e) => setEmail(e.target.value)}
              onFocus={focus} onBlur={blur} style={input}
            />
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label htmlFor="password" style={label}>Password</label>
          <div style={{ position: "relative" }}>
            <Lock size={16} style={icon} />
            <input
              id="password" type={showPw ? "text" : "password"} value={password} placeholder="Enter your password"
              autoComplete="current-password" onChange={(e) => setPassword(e.target.value)}
              onFocus={focus} onBlur={blur} style={{ ...input, paddingRight: 60 }}
            />
            <button
              type="button" onClick={() => setShowPw((s) => !s)}
              aria-label={showPw ? "Hide password" : "Show password"}
              style={{
                position: "absolute", right: 5, top: 5, height: 32, padding: "0 9px",
                border: "none", background: "transparent", borderRadius: 7, cursor: "pointer",
                fontFamily: FONT, fontSize: 12, fontWeight: 600, color: MUTED,
              }}
            >
              {showPw ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "0 0 16px", fontSize: 12.5 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 7, margin: 0, fontWeight: 500, color: MUTED, cursor: "pointer" }}>
            <input
              type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
              style={{ width: 15, height: 15, accentColor: NAVY }}
            />
            Keep me signed in
          </label>
          <a href="/forgot-password" style={{ color: NAVY, fontWeight: 600, textDecoration: "none" }}>Forgot password?</a>
        </div>

        <button
          type="submit" disabled={busy}
          style={{
            width: "100%", height: 44, border: "none", borderRadius: 10,
            background: busy ? "#64748B" : `linear-gradient(180deg, #14306E, ${NAVY})`,
            boxShadow: busy ? "none" : `0 8px 18px -8px rgba(10,31,77,.7), inset 0 -3px 0 ${GOLD}`,
            color: "#fff", fontSize: 14.5, fontWeight: 700, fontFamily: FONT,
            cursor: busy ? "not-allowed" : "pointer",
          }}
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>

        <p style={{
          margin: "16px 0 0", paddingTop: 12, borderTop: `1px solid ${BORDER}`,
          textAlign: "center", fontSize: 12, color: MUTED,
        }}>
          Need access? <a href="/contact-admin" style={{ color: NAVY, fontWeight: 600, textDecoration: "none" }}>Contact your administrator</a>
        </p>
      </form>
    </div>
  );
}